import { RefObject, useCallback, useEffect, useMemo, useState } from 'react'

import { Box, Header, IconButton } from '@revolut-internal/ui-kit'

import { RtmPermission } from 'common/auth/auth'
import { hasPermission } from 'common/security/permissionsChecks'
import { Security } from 'common/security/security'
import { TabBar } from 'modules/Monitoring/components/TabBar/TabBar'
import {
  AgentsFilterType,
  AlertingPolicyGetResponse,
  IncidentsFilterType,
  ResourceFilterType,
  ResourcesSideParams,
  SpecialisationHealth,
  TicketsFilterType,
} from 'modules/Monitoring/types'
import { uuid } from 'modules/Monitoring/utils/uuid'

import { QueueHealthAgents } from './agents/QueueHealthAgents'
import { QueueHealthIncidents } from './incidents/QueueHealthIncidents'
import { QueueHealthTickets } from './tickets/QueueHealthTickets'

export type QueueHealthResources = 'agents' | 'tickets' | 'incidents'
export type QueueHealthAllResourcesProps = {
  scrollRef: RefObject<HTMLDivElement | null>
  queueHealthSpecialisations?: SpecialisationHealth[]
  resourcesSideParams?: ResourcesSideParams
  policy?: AlertingPolicyGetResponse | null
  isPolicyPending: boolean
  onResourceChange: (value: string) => void
  onPolicyShow: VoidFunction
}

const ticketTabsPermission: RtmPermission = 'analytics.view.agent_tickets.list'
const agentTabsPermission: RtmPermission = 'analytics.create.agents.attendance.short'

const TicketsFilterTypes = Object.values(TicketsFilterType) as string[]
const AgentsFilterTypes = Object.values(AgentsFilterType) as string[]
const TABS_CONFIG = {
  [ResourceFilterType.Incidents]: {
    label: 'Incidents',
    permission: 'alerting.view.policies.list',
  },
  [ResourceFilterType.Unassigned]: {
    label: 'Unassigned',
    permission: ticketTabsPermission,
  },
  [ResourceFilterType.Assigned]: {
    label: 'Assigned',
    permission: ticketTabsPermission,
  },
  [ResourceFilterType.Pending]: {
    label: 'Pending',
    permission: ticketTabsPermission,
  },
  [ResourceFilterType.BreachedQwt]: {
    label: 'Breached QWT',
    permission: ticketTabsPermission,
  },
  [ResourceFilterType.Online]: {
    label: 'Online',
    permission: agentTabsPermission,
  },
  [ResourceFilterType.WrapUp]: {
    label: 'Wrapping up',
    permission: agentTabsPermission,
  },
  [ResourceFilterType.Scheduled]: {
    label: 'Scheduled',
    permission: agentTabsPermission,
  },
  [ResourceFilterType.Offline]: {
    label: 'Offline',
    permission: agentTabsPermission,
  },
  [ResourceFilterType.OnBreak]: {
    label: 'On break',
    permission: agentTabsPermission,
  },
  [ResourceFilterType.Absent]: {
    label: 'Absent',
    permission: agentTabsPermission,
  },
  [ResourceFilterType.Overtime]: {
    label: 'Overtime',
    permission: agentTabsPermission,
  },
}
const tabs = Object.entries(TABS_CONFIG).map(([value, { label }]) => ({
  value,
  label,
}))

export const QueueHealthAllResources = ({
  scrollRef,
  queueHealthSpecialisations,
  resourcesSideParams,
  policy,
  isPolicyPending,
  onResourceChange,
  onPolicyShow,
}: QueueHealthAllResourcesProps) => {
  const [tabsConfig, setTabsConfig] = useState({
    key: uuid(),
    isManuallyChanging: false,
  })
  const [selectedTab, setSelectedTab] = useState<string | undefined>(
    resourcesSideParams?.filterType,
  )
  const open = Boolean(resourcesSideParams)

  const filteredTabs = useMemo(
    () =>
      tabs.filter(
        tab =>
          hasPermission(TABS_CONFIG[tab.value]?.permission) &&
          (Boolean(resourcesSideParams?.specialisation) ||
            tab.value !== IncidentsFilterType.Incidents),
      ),
    [resourcesSideParams?.specialisation],
  )

  const currentResource: QueueHealthResources | undefined = useMemo(() => {
    const filterType = resourcesSideParams?.filterType

    if (!filterType) {
      return undefined
    }
    if (TicketsFilterTypes.includes(filterType)) {
      return 'tickets'
    }
    if (AgentsFilterTypes.includes(filterType)) {
      return 'agents'
    }
    if (filterType === IncidentsFilterType.Incidents) {
      return 'incidents'
    }

    return undefined
  }, [resourcesSideParams?.filterType])

  useEffect(() => {
    if (!resourcesSideParams?.filterType) {
      return
    }

    setSelectedTab(resourcesSideParams.filterType)
  }, [resourcesSideParams?.filterType])

  useEffect(() => {
    if (open) {
      return
    }

    setSelectedTab(undefined)
  }, [open])

  // We need to change tabs key when its value changes from the outside to reset
  // the tabs scroll position, otherwise it would stay on the previous place
  useEffect(() => {
    if (tabsConfig.isManuallyChanging) {
      setTabsConfig(prevState => ({
        ...prevState,
        isManuallyChanging: false,
      }))
      return
    }

    setTabsConfig({
      key: uuid(),
      isManuallyChanging: false,
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab])

  const handleTabChange = useCallback(
    (value: string | null) => {
      if (!value) {
        return
      }

      onResourceChange(value)
      setSelectedTab(value)
      setTabsConfig(prevState => ({
        ...prevState,
        isManuallyChanging: true,
      }))
    },
    [onResourceChange],
  )

  return (
    <>
      <Header>
        <Header.CloseButton aria-label="Close" />
        <Header.Title>
          {resourcesSideParams?.specialisation?.name ?? resourcesSideParams?.queue?.name}
        </Header.Title>
        {((policy && Security.canViewPolicies) || Security.canCreatePolicy) && (
          <Header.Actions>
            <IconButton useIcon="DrawParallelChannel" onClick={onPolicyShow}>
              {policy ? 'Modify' : 'Create'} policy
            </IconButton>
          </Header.Actions>
        )}
      </Header>

      <Box mt="-s-8">
        {selectedTab && (
          <Box mb="s-16">
            <TabBar
              key={tabsConfig.key}
              behaviour="scroll"
              behaviourVariant="action"
              tabs={filteredTabs}
              value={selectedTab}
              defaultValue={selectedTab}
              onChange={handleTabChange}
            />
          </Box>
        )}

        {resourcesSideParams ? (
          <>
            {currentResource === 'incidents' && (
              <QueueHealthIncidents
                resourcesSideParams={resourcesSideParams}
                policy={policy}
                isPolicyPending={isPolicyPending}
                onSetPolicy={onPolicyShow}
              />
            )}
            {currentResource === 'agents' && (
              <QueueHealthAgents
                scrollRef={scrollRef}
                resourcesSideParams={resourcesSideParams}
                queueHealthSpecialisations={queueHealthSpecialisations}
              />
            )}
            {currentResource === 'tickets' && (
              <QueueHealthTickets
                scrollRef={scrollRef}
                resourcesSideParams={resourcesSideParams}
              />
            )}
          </>
        ) : null}
      </Box>
    </>
  )
}
